import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { types as sdkTypes, util as sdkUtil } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { trackIntercomEvent } from '../../util/intercom';
import { calculatePortfolioTier } from '../../util/api';
import * as log from '../../util/log';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchSkillsAndTools } from '../../ducks/SkillsAndTools.duck';

import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';

const { UUID } = sdkTypes;

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //

export const SHOW_PORTFOLIO_WORK_REQUEST = 'PortfolioWorkPage/SHOW_PORTFOLIO_WORK_REQUEST';
export const SHOW_PORTFOLIO_WORK_SUCCESS = 'PortfolioWorkPage/SHOW_PORTFOLIO_WORK_SUCCESS';
export const SHOW_PORTFOLIO_WORK_ERROR = 'PortfolioWorkPage/SHOW_PORTFOLIO_WORK_ERROR';
export const PUBLISH_PORTFOLIO_WORK_REQUEST = 'PortfolioWork/PUBLISH_PORTFOLIO_WORK_REQUEST';
export const PUBLISH_PORTFOLIO_WORK_SUCCESS = 'PortfolioWork/PUBLISH_PORTFOLIO_WORK_SUCCESS';
export const PUBLISH_PORTFOLIO_WORK_ERROR = 'PortfolioWork/PUBLISH_PORTFOLIO_WORK_ERROR';
export const CLOSE_PORTFOLIO_WORK_REQUEST = 'PortfolioWork/CLOSE_PORTFOLIO_WORK_REQUEST';
export const CLOSE_PORTFOLIO_WORK_SUCCESS = 'PortfolioWork/CLOSE_PORTFOLIO_WORK_SUCCESS';
export const CLOSE_PORTFOLIO_WORK_ERROR = 'PortfolioWork/CLOSE_PORTFOLIO_WORK_ERROR';
export const RESET_PORTFOLIO_WORK_INITIAL_VALUES =
  'PortfolioWorkPage/RESET_PORTFOLIO_WORK_INITIAL_VALUES';

// ================ Reducer ================ //

const initialState = {
  id: null,
  error: null,
  loading: false,
  initialValues: null, // Add this if not present
};
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SHOW_PORTFOLIO_WORK_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case SHOW_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        id: payload.id,
        loading: false,
      };
    case SHOW_PORTFOLIO_WORK_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case PUBLISH_PORTFOLIO_WORK_REQUEST:
      return { ...state, portfolioWorkInProgress: true, portfolioWorkError: null };
    case PUBLISH_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        portfolioWork: payload,
        portfolioWorkInProgress: false,
      };
    case PUBLISH_PORTFOLIO_WORK_ERROR:
      return { ...state, portfolioWorkInProgress: false, portfolioWorkError: payload };
    case CLOSE_PORTFOLIO_WORK_REQUEST:
      return { ...state, portfolioWorkInProgress: true, portfolioWorkError: null };
    case CLOSE_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        portfolioWork: payload,
        portfolioWorkInProgress: false,
      };
    case CLOSE_PORTFOLIO_WORK_ERROR:
      return { ...state, portfolioWorkInProgress: false, portfolioWorkError: payload };
    case RESET_PORTFOLIO_WORK_INITIAL_VALUES:
      return {
        ...state,
        initialValues: null,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const showPortfolioWorkRequest = requestAction(SHOW_PORTFOLIO_WORK_REQUEST);
export const showPortfolioWorkSuccess = successAction(SHOW_PORTFOLIO_WORK_SUCCESS);
export const showPortfolioWorkError = errorAction(SHOW_PORTFOLIO_WORK_ERROR);
export const publishPortfolioWorkRequest = requestAction(PUBLISH_PORTFOLIO_WORK_REQUEST);
export const publishPortfolioWorkSuccess = successAction(PUBLISH_PORTFOLIO_WORK_SUCCESS);
export const publishPortfolioWorkError = errorAction(PUBLISH_PORTFOLIO_WORK_ERROR);
export const closePortfolioWorkRequest = requestAction(
  'PortfolioWork/CLOSE_PORTFOLIO_WORK_REQUEST'
);
export const closePortfolioWorkSuccess = successAction(
  'PortfolioWork/CLOSE_PORTFOLIO_WORK_SUCCESS'
);
export const closePortfolioWorkError = errorAction('PortfolioWork/CLOSE_PORTFOLIO_WORK_ERROR');
export const resetPortfolioWorkInitialValues = () => ({
  type: RESET_PORTFOLIO_WORK_INITIAL_VALUES,
});

// ================ Thunk ================ //

export const showPortfolioWork = workId => (dispatch, getState, sdk) => {
  dispatch(showPortfolioWorkRequest(workId));

  const params = {
    id: workId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Listing page
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
      'variants.landscape-crop6x',
      'variants.horizontal',
      'variants.horizontal-2x',
      'variants.vertical',
      'variants.vertical-2x',
      'variants.portrait45',
      'variants.portrait45-2x',
      'variants.portrait34',
      'variants.portrait34-2x',
      'variants.square',
      'variants.square2x',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
      // Social media
      'variants.facebook',
      'variants.twitter',
    ],
    'imageVariant.horizontal': sdkUtil.objectQueryString({
      w: 1920,
      h: 1080,
      fit: 'scale',
    }),
    'imageVariant.horizontal-2x': sdkUtil.objectQueryString({
      w: 2560,
      h: 1440,
      fit: 'scale',
    }),
    'imageVariant.vertical': sdkUtil.objectQueryString({
      w: 1080,
      h: 1920,
      fit: 'scale',
    }),
    'imageVariant.vertical-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 2560,
      fit: 'scale',
    }),
    'imageVariant.portrait45': sdkUtil.objectQueryString({
      w: 1080,
      h: 1350,
      fit: 'scale',
    }),
    'imageVariant.portrait45-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1800,
      fit: 'scale',
    }),
    'imageVariant.portrait34': sdkUtil.objectQueryString({
      w: 1080,
      h: 1440,
      fit: 'scale',
    }),
    'imageVariant.portrait34-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1920,
      fit: 'scale',
    }),
    'imageVariant.square': sdkUtil.objectQueryString({
      w: 1080,
      h: 1080,
      fit: 'scale',
    }),
    'imageVariant.square2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1440,
      fit: 'scale',
    }),
  };

  return sdk.listings
    .show(params)
    .then(response => {
      dispatch(showPortfolioWorkSuccess(response));
      dispatch(addMarketplaceEntities(response));

      return response;
    })
    .catch(e => {
      dispatch(showPortfolioWorkError(e));
      throw e;
    });
};

export const showOwnPortfolioWork = workId => (dispatch, getState, sdk) => {
  dispatch(showPortfolioWorkRequest(workId));

  const params = {
    id: workId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Listing page
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
      'variants.landscape-crop6x',
      'variants.horizontal',
      'variants.horizontal-2x',
      'variants.vertical',
      'variants.vertical-2x',
      'variants.portrait45',
      'variants.portrait45-2x',
      'variants.portrait34',
      'variants.portrait34-2x',
      'variants.square',
      'variants.square2x',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
      // Social media
      'variants.facebook',
      'variants.twitter',
    ],
    'imageVariant.horizontal': sdkUtil.objectQueryString({
      w: 1920,
      h: 1080,
      fit: 'scale',
    }),
    'imageVariant.horizontal-2x': sdkUtil.objectQueryString({
      w: 2560,
      h: 1440,
      fit: 'scale',
    }),
    'imageVariant.vertical': sdkUtil.objectQueryString({
      w: 1080,
      h: 1920,
      fit: 'scale',
    }),
    'imageVariant.vertical-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 2560,
      fit: 'scale',
    }),
    'imageVariant.portrait45': sdkUtil.objectQueryString({
      w: 1080,
      h: 1350,
      fit: 'scale',
    }),
    'imageVariant.portrait45-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1800,
      fit: 'scale',
    }),
    'imageVariant.portrait34': sdkUtil.objectQueryString({
      w: 1080,
      h: 1440,
      fit: 'scale',
    }),
    'imageVariant.portrait34-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1920,
      fit: 'scale',
    }),
    'imageVariant.square': sdkUtil.objectQueryString({
      w: 1080,
      h: 1080,
      fit: 'scale',
    }),
    'imageVariant.square2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1440,
      fit: 'scale',
    }),
  };

  return sdk.ownListings
    .show(params)
    .then(response => {
      dispatch(showPortfolioWorkSuccess(response));
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => {
      dispatch(showPortfolioWorkError(e));
      throw e;
    });
};

export const publishPortfolioWork = (listingId, currentUser) => {
  return (dispatch, getState, sdk) => {
    dispatch(publishPortfolioWorkRequest(listingId));

    return sdk.ownListings
      .publishDraft({ id: listingId }, { expand: true })
      .then(async response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(publishPortfolioWorkSuccess(response));

        const userId = getState().user.currentUser?.id?.uuid;
        if (userId) {
          try {
            await calculatePortfolioTier(userId);
            await dispatch(fetchCurrentUser());
          } catch (e) {
            log.error(e, 'calculate-portfolio-tier-failed', { userId });
          }
        }

        return response;
      })
      .then(() => {
        trackIntercomEvent('Portfolio Work Published', {
          listingId: listingId.uuid,
        });
      })
      .catch(e => {
        return dispatch(publishPortfolioWorkError(storableError(e)));
      });
  };
};

export const closePortfolioWork = workId => {
  return async (dispatch, getState, sdk) => {
    dispatch(closePortfolioWorkRequest(workId));

    try {
      // Fetch the listing to check its state
      const showResponse = await sdk.ownListings.show({ id: workId });
      const listing = showResponse.data.data;

      // If draft, publish first
      let publishedResponse = null;
      if (listing.attributes.state === 'draft') {
        publishedResponse = await sdk.ownListings.publishDraft({ id: workId }, { expand: true });
        dispatch(addMarketplaceEntities(publishedResponse));
      }

      // Close the listing (use publishedResponse if just published, otherwise use showResponse)
      const closeResponse = await sdk.ownListings.close({ id: workId }, { expand: true });
      dispatch(addMarketplaceEntities(closeResponse));
      dispatch(closePortfolioWorkSuccess(closeResponse));

      const userId = getState().user.currentUser?.id?.uuid;
      if (userId) {
        try {
          await calculatePortfolioTier(userId);
          await dispatch(fetchCurrentUser());
        } catch (err) {
          log.error(err, 'calculate-portfolio-tier-failed', { userId });
        }
      }

      return closeResponse;
    } catch (e) {
      return dispatch(closePortfolioWorkError(storableError(e)));
    }
  };
};

export const loadData = params => (dispatch, getState, sdk) => {
  dispatch(resetPortfolioWorkInitialValues());

  const workId = new UUID(params.id);

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];

  const state = getState();
  const skills = state.SkillsAndTools.skillOptions;
  const tools = state.SkillsAndTools.toolOptions;

  const fetchSkillsAndToolsIfNeeded = async () => {
    if (skills.length === 0 || tools.length === 0) {
      await dispatch(fetchSkillsAndTools());
    }
  };

  return Promise.all([
    ownListingVariants.includes(params.type)
      ? dispatch(showOwnPortfolioWork(workId))
      : dispatch(showPortfolioWork(workId)),
    dispatch(fetchCurrentUser()),
    fetchSkillsAndToolsIfNeeded(),
  ])
    .then(response => {
      return response;
    })
    .catch(e => {
      throw e;
    });
};
